import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, inr, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Package, AlertTriangle, ArrowDown, ArrowUp, Trash } from "lucide-react";

export default function AdminInventory() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [moveOpen, setMoveOpen] = useState(null);
  const [moveData, setMoveData] = useState({ type: "in", qty: 1, reason: "" });
  const [waste, setWaste] = useState([]);

  const load = () => {
    api.get("/inventory").then(r => setItems(r.data));
    api.get("/inventory/waste-summary").then(r => setWaste(r.data)).catch(()=>{});
  };
  useEffect(load, []);

  const save = async () => {
    try {
      const payload = {
        ...editing,
        current_stock: Number(editing.current_stock),
        low_stock_threshold: Number(editing.low_stock_threshold),
        cost_per_unit: Number(editing.cost_per_unit),
      };
      if (editing.id) await api.put(`/inventory/${editing.id}`, payload);
      else await api.post("/inventory", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const recordMove = async () => {
    try {
      await api.post(`/inventory/${moveOpen.id}/movement`, { ...moveData, qty: Number(moveData.qty) });
      toast.success(`${moveData.type.toUpperCase()} recorded`);
      setMoveOpen(null); setMoveData({ type: "in", qty: 1, reason: "" });
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/inventory/${id}`); toast.success("Deleted"); load();
  };

  const totalValue = items.reduce((s, i) => s + (i.current_stock || 0) * (i.cost_per_unit || 0), 0);
  const lowCount = items.filter(i => (i.current_stock||0) <= (i.low_stock_threshold||0)).length;

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Inventory</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Raw materials & stock</h1>
          </div>
          <button data-testid="new-inv-btn" onClick={() => setEditing({ name: "", unit: "kg", current_stock: 0, low_stock_threshold: 5, cost_per_unit: 0 })}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600">
            <Plus size={16} /> New item
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Total items</div>
            <div className="font-heading text-3xl font-extrabold mt-2">{items.length}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Stock value</div>
            <div className="font-heading text-3xl font-extrabold mt-2">{inr(totalValue)}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">Low stock</div>
            <div className="font-heading text-3xl font-extrabold mt-2 text-amber-700">{lowCount}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-ink-500">Waste tracked</div>
            <div className="font-heading text-3xl font-extrabold mt-2">{waste.length}</div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-200 text-ink-700">
              <tr>
                <th className="p-3 text-left font-bold uppercase text-xs tracking-wider">Item</th>
                <th className="p-3 text-right font-bold uppercase text-xs tracking-wider">Stock</th>
                <th className="p-3 text-right font-bold uppercase text-xs tracking-wider">Threshold</th>
                <th className="p-3 text-right font-bold uppercase text-xs tracking-wider">Cost/unit</th>
                <th className="p-3 text-right font-bold uppercase text-xs tracking-wider">Value</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => {
                const low = (it.current_stock||0) <= (it.low_stock_threshold||0);
                return (
                  <tr key={it.id} className={`border-t border-border ${low ? 'bg-amber-50' : ''}`} data-testid={`inv-row-${it.id}`}>
                    <td className="p-3 font-semibold">
                      <div className="flex items-center gap-2"><Package size={14} className="text-ink-500" />{it.name}</div>
                    </td>
                    <td className="p-3 text-right">
                      <span className={low ? 'text-amber-800 font-bold' : 'font-semibold'}>{it.current_stock}</span> {it.unit}
                      {low && <AlertTriangle size={14} className="inline ml-1 text-amber-700" />}
                    </td>
                    <td className="p-3 text-right text-ink-500">{it.low_stock_threshold} {it.unit}</td>
                    <td className="p-3 text-right">{inr(it.cost_per_unit)}</td>
                    <td className="p-3 text-right font-bold">{inr((it.current_stock||0) * (it.cost_per_unit||0))}</td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-1">
                        <button data-testid={`move-${it.id}`} onClick={() => { setMoveOpen(it); setMoveData({ type: "in", qty: 1, reason: "" }); }}
                          className="px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-100 text-emerald-900 hover:bg-emerald-200">Move</button>
                        <button onClick={() => setEditing(it)} className="px-2.5 py-1 rounded-md text-xs font-bold bg-cream-200 hover:bg-cream-300">Edit</button>
                        <button onClick={() => remove(it.id)} className="px-2 py-1 rounded-md bg-red-50 hover:bg-red-100 text-brand"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {waste.length > 0 && (
          <div className="mt-5 bg-white rounded-xl p-5 border border-border">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700 mb-2">Waste summary</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {waste.map(w => (
                <div key={w.item} className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                  <div className="font-semibold text-sm">{w.item}</div>
                  <div className="text-xs text-amber-800">{w.qty_wasted} wasted · {w.events} events</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-2xl font-bold text-ink">{editing.id ? "Edit item" : "New item"}</div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              {[["name","Name","text"],["unit","Unit","text"],["current_stock","Current Stock","number"],["low_stock_threshold","Low threshold","number"],["cost_per_unit","Cost per unit (₹)","number"]].map(([k,l,t]) => (
                <div key={k} className={k==="name"?"col-span-2":""}>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">{l}</label>
                  <input data-testid={`inv-form-${k}`} type={t} value={editing[k] ?? ""} onChange={e => setEditing({...editing, [k]: e.target.value})}
                    className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full font-semibold text-ink-700 hover:bg-cream-200">Cancel</button>
              <button data-testid="inv-form-save" onClick={save} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}

      {moveOpen && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center px-4" onClick={() => setMoveOpen(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="font-heading text-2xl font-bold text-ink">Stock movement</div>
            <div className="text-sm text-ink-500 mt-1">{moveOpen.name} · current: {moveOpen.current_stock} {moveOpen.unit}</div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[
                {k: "in", label: "Stock in", icon: ArrowDown, cls: "bg-emerald-100 text-emerald-900 border-emerald-300"},
                {k: "out", label: "Stock out", icon: ArrowUp, cls: "bg-blue-100 text-blue-900 border-blue-300"},
                {k: "waste", label: "Waste", icon: Trash, cls: "bg-amber-100 text-amber-900 border-amber-300"},
              ].map(t => {
                const Icon = t.icon;
                const active = moveData.type === t.k;
                return (
                  <button key={t.k} data-testid={`move-type-${t.k}`} onClick={() => setMoveData({...moveData, type: t.k})}
                    className={`p-3 rounded-xl border-2 ${active ? t.cls + ' border-current' : 'bg-white border-border'}`}>
                    <Icon size={18} className="mx-auto mb-1" />
                    <div className="text-xs font-bold">{t.label}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-4">
              <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Quantity ({moveOpen.unit})</label>
              <input data-testid="move-qty" type="number" value={moveData.qty} onChange={e => setMoveData({...moveData, qty: e.target.value})}
                className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            </div>
            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Reason</label>
              <input data-testid="move-reason" value={moveData.reason} onChange={e => setMoveData({...moveData, reason: e.target.value})}
                className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setMoveOpen(null)} className="px-5 py-2.5 rounded-full text-ink-700 font-semibold hover:bg-cream-200">Cancel</button>
              <button data-testid="move-save" onClick={recordMove} className="px-5 py-2.5 rounded-full bg-brand text-white font-semibold">Record</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
